// File: src/store/slices/savedQuestionsSlice.js
// Purpose: Redux Toolkit slice for saved questions state management
// Last modified: 2025-11-06

import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import apiService from '../../services/api';
import packageCache from '../../utils/packageCache';

// ==================== Async Thunks ====================

/**
 * Fetch all saved questions
 */
export const fetchSavedQuestions = createAsyncThunk(
    'savedQuestions/fetchAll',
    async (_, { rejectWithValue }) => {
        try {
            const data = await apiService.savedQuestions.getAll();
            return data;
        } catch (error) {
            console.error('[fetchSavedQuestions] Error:', error);
            return rejectWithValue(error.message);
        }
    }
);

/**
 * Save a question
 */
export const saveQuestion = createAsyncThunk(
    'savedQuestions/save',
    async ({ questionId, packageId }, { rejectWithValue }) => {
        try {
            await apiService.savedQuestions.save(questionId);

            // Update cache
            if (packageId) {
                packageCache.updateQuestionSavedStatus(packageId, questionId, true);
            }

            return { questionId, saved: true };
        } catch (error) {
            console.error('[saveQuestion] Error:', error);
            return rejectWithValue(error.message);
        }
    }
);

/**
 * Delete a saved question
 */
export const deleteSavedQuestion = createAsyncThunk(
    'savedQuestions/delete',
    async ({ questionId, packageId }, { rejectWithValue }) => {
        try {
            await apiService.savedQuestions.delete(questionId);

            // Update cache
            if (packageId) {
                packageCache.updateQuestionSavedStatus(packageId, questionId, false);
            }

            return { questionId, saved: false };
        } catch (error) {
            console.error('[deleteSavedQuestion] Error:', error);
            return rejectWithValue(error.message);
        }
    }
);

// ==================== Slice ====================

const initialState = {
    savedQuestions: [],
    loading: false,
    error: null,
    actionLoading: {}, // Track loading state for individual save/delete actions
};

const savedQuestionsSlice = createSlice({
    name: 'savedQuestions',
    initialState,
    reducers: {
        clearError: (state) => {
            state.error = null;
        },
        // Local update for optimistic UI
        toggleQuestionSaved: (state, action) => {
            const { questionId, saved } = action.payload;
            if (saved) {
                // Add to saved questions (if we have the full question object)
                // This is for optimistic updates
            } else {
                // Remove from saved questions
                state.savedQuestions = state.savedQuestions.filter(
                    q => q.id !== questionId
                );
            }
        },
    },
    extraReducers: (builder) => {
        builder
            // Fetch all saved questions
            .addCase(fetchSavedQuestions.pending, (state) => {
                state.loading = true;
                state.error = null;
            })
            .addCase(fetchSavedQuestions.fulfilled, (state, action) => {
                state.loading = false;
                state.savedQuestions = action.payload;
            })
            .addCase(fetchSavedQuestions.rejected, (state, action) => {
                state.loading = false;
                state.error = action.payload;
                state.savedQuestions = [];
            })
            // Save question
            .addCase(saveQuestion.pending, (state, action) => {
                state.actionLoading[action.meta.arg.questionId] = true;
            })
            .addCase(saveQuestion.fulfilled, (state, action) => {
                state.actionLoading[action.payload.questionId] = false;
            })
            .addCase(saveQuestion.rejected, (state, action) => {
                state.actionLoading[action.meta.arg.questionId] = false;
                state.error = action.payload;
            })
            // Delete saved question
            .addCase(deleteSavedQuestion.pending, (state, action) => {
                state.actionLoading[action.meta.arg.questionId] = true;
            })
            .addCase(deleteSavedQuestion.fulfilled, (state, action) => {
                state.actionLoading[action.payload.questionId] = false;
                // Remove from local state
                state.savedQuestions = state.savedQuestions.filter(
                    q => q.id !== action.payload.questionId
                );
            })
            .addCase(deleteSavedQuestion.rejected, (state, action) => {
                state.actionLoading[action.meta.arg.questionId] = false;
                state.error = action.payload;
            });
    },
});

// ==================== Exports ====================

export const { clearError, toggleQuestionSaved } = savedQuestionsSlice.actions;

// Selectors
export const selectSavedQuestions = (state) => state.savedQuestions.savedQuestions;
export const selectSavedQuestionsLoading = (state) => state.savedQuestions.loading;
export const selectSavedQuestionsError = (state) => state.savedQuestions.error;
export const selectQuestionActionLoading = (questionId) => (state) =>
    state.savedQuestions.actionLoading[questionId] || false;

// Helper selector to check if a question is saved
export const selectIsQuestionSaved = (questionId) => (state) =>
    state.savedQuestions.savedQuestions.some(q => q.id === questionId);

export default savedQuestionsSlice.reducer;
